import React from 'react';
import { useUpdateEffect } from 'react-use';
import { FormikConfig, FormikValues, useFormik } from 'formik';
import { debounce } from 'lodash';

type UseDebouncedValidationFormikProps<Values extends FormikValues = FormikValues> = {
  debounceTime?: number;
} & FormikConfig<Values>;

export function useDebouncedValidationFormik<Values extends FormikValues>({
  debounceTime = 300,
  ...formikProps
}: UseDebouncedValidationFormikProps<Values>) {
  const formik = useFormik({
    ...formikProps,
    validateOnChange: false,
  });

  const debouncedValidate = React.useMemo(
    () => debounce(formik.validateForm, debounceTime),
    [formik.validateForm, debounceTime],
  );

  useUpdateEffect(() => {
    if (formik.isSubmitting) {
      debouncedValidate.cancel();
    }
  }, [formik.isSubmitting, debouncedValidate]);

  useUpdateEffect(() => {
    debouncedValidate(formik.values);
  }, [formik.values, debouncedValidate]);

  return formik;
}
